/* eslint-disable indent */
import { Step } from 'react-joyride'

import { TourType } from './types'

export const getTourSteps = (
  t: (key: string, defaultValue?: string) => string,
  isMobileSideMenu: boolean,
  isMobile: boolean,
): Record<TourType, Step[]> => {
  const baseDashboardSteps: Step[] = [
    {
      target: '.profile-avatar-button',
      content: t(
        'tour.dashboard.profile',
        'Click here to access your personal and trading profile settings, including security options and logout.',
      ),
      placement: 'bottom-end',
      disableBeacon: true,
    },
    {
      target: '.filter-account',
      content: t(
        'tour.dashboard.accountFilter',
        'Use this filter to select the trading account you want to view analytics for. Analytics will update based on the selected account.',
      ),
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '.period-filter-button',
      content: t(
        'tour.dashboard.periodFilter',
        'Click this filter to choose the period for which you want to view trading analytics.',
      ),
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '.manual-import-button',
      content: t(
        'tour.dashboard.manualImport',
        'To analyze your trades, start by uploading your trading history. Click this button to begin the manual import process.',
      ),
      placement: 'left',
      disableBeacon: true,
    },
    // {
    //   target: '.menu-webinars',
    //   content: t(
    //     'tour.dashboard.webinars',
    //     'The “Webinars” section includes educational webinars and masterclasses led by trading professionals.',
    //   ),
    //   placement: 'right',
    //   disableBeacon: true,
    // },
    ...(!isMobileSideMenu
      ? [
          {
            target: '.menu-dashboard',
            content: t(
              'tour.dashboard.dashboard',
              'The “Dashboard” tab includes key widgets, a trading calendar, and a table with open and closed positions.',
            ),
            placement: 'right' as const,
            disableBeacon: true,
          },
          {
            target: '.menu-daily-stats',
            content: t(
              'tour.dashboard.dailyStats',
              'The “Daily Stats” section shows key metrics and detailed trading info for each day. You can access it from the sidebar or the calendar on the dashboard.',
            ),
            placement: 'right' as const,
            disableBeacon: true,
          },
          {
            target: '.menu-ai-market-insights',
            content: t('tour.dashboard.aiMarketInsights'),
            placement: 'right' as const,
            disableBeacon: true,
          },
          {
            target: '.menu-trading-plan',
            content: t('tour.dashboard.tradingPlan'),
            placement: 'right' as const,
            disableBeacon: true,
          },
          {
            target: '.menu-trading-log',
            content: t(
              'tour.dashboard.tradingLog',
              'The “Trading Log” shows detailed info for each trade.',
            ),
            placement: 'right' as const,
            disableBeacon: true,
          },
          {
            target: '.menu-cash-flow',
            content: t(
              'tour.dashboard.cashFlowMenu',
              'The “Cash Flow” section shows deposits, withdrawals, credits, and other balance adjustments for the selected account.',
            ),
            placement: 'right' as const,
            disableBeacon: true,
          },
          {
            target: '.menu-reports',
            content: t(
              'tour.dashboard.reports',
              'The “Reports” section includes 5 tabs with analytics: time, trade duration, instruments, psychology, and strategy.',
            ),
            placement: 'right' as const,
            disableBeacon: true,
          },
          // {
          //   target: '.menu-education',
          //   content: t(
          //     'tour.dashboard.education',
          //     'The “Education” section contains a structured learning program with three modules: Foundation, Intermediate, and Advanced.',
          //   ),
          //   placement: 'right' as const,
          //   disableBeacon: true,
          // },
          {
            target: '.menu-contact',
            content: t(
              'tour.dashboard.support',
              'Need help or have a question? Click here to send a message to our support team.',
            ),
            placement: 'top' as const,
            disableBeacon: true,
          },
        ]
      : []),
  ]

  const dashboardElementsSteps: Step[] = [
    {
      target: isMobile ? '.tab-widgets' : '.dashboard-widgets',
      content: t(
        'tour.dashboard.widgets',
        'This section displays key trading metrics such as profit, ROI, average trade size, and holding time. You can rearrange or hide widgets using the “Customize” button.',
      ),
      placement: isMobile ? 'bottom' : 'top-start',
      disableBeacon: true,
      ...(isMobile && { data: { open: '.tab-widgets' } }),
    },
    {
      target: isMobile ? '.tab-calendar' : '.dashboard-calendar',
      content: t(
        'tour.dashboard.calendar',
        'The calendar helps you view trading results for each day. Click a cell to open detailed statistics, or use arrows to navigate between months.',
      ),
      placement: isMobile ? 'bottom' : 'top-start',
      disableBeacon: true,
      ...(isMobile && { data: { open: '.tab-calendar' } }),
    },
    {
      target: isMobile ? '.tab-positions' : '.dashboard-positions-table',
      content: t(
        'tour.dashboard.positionsTable',
        'This table shows open and closed positions. Use the tabs above to switch between them and view trade history.',
      ),
      placement: isMobile ? 'bottom' : 'top-start',
      disableBeacon: true,
      ...(isMobile && { data: { open: '.tab-positions' } }),
    },
    {
      target: '.tab-cash-flows',
      content: t(
        'tour.dashboard.cashFlow',
        'Use this tab to review deposits, withdrawals, credits, and other cash movements for the selected account.',
      ),
      placement: isMobile ? 'bottom' : 'top-start',
      disableBeacon: true,
      ...(isMobile && { data: { open: '.tab-positions' } }),
    },
  ]

  return {
    dashboard: [...baseDashboardSteps, ...dashboardElementsSteps],

    profile: [
      {
        target: '.tab-personalInfo',
        content: t('tour.profile.personalInfo', 'This tab contains your personal profile details.'),
        placement: 'bottom',
        disableBeacon: true,
        data: { open: '.tab-personalInfo' },
      },
      {
        target: '.tab-tradingInformation',
        content: t(
          'tour.profile.tradingInfo',
          'This section is where you manage your trading preferences.',
        ),
        placement: 'bottom',
        disableBeacon: true,
        data: { open: '.tab-tradingInformation' },
      },
      {
        target: '.tab-security',
        content: t('tour.profile.security', 'Manage your login and security preferences here.'),
        placement: 'bottom',
        disableBeacon: true,
        data: { open: '.tab-security' },
      },
    ],

    settings: [
      {
        target: '.tab-accounts',
        content: t('tour.settings.accounts', 'Add, edit or remove your trading accounts here.'),
        placement: 'bottom',
        disableBeacon: true,
        data: { open: '.tab-accounts' },
      },
      {
        target: '.tab-logHistory',
        content: t('tour.settings.logHistory', 'View the history of actions you have performed.'),
        placement: 'bottom',
        disableBeacon: true,
        data: { open: '.tab-logHistory' },
      },
      {
        target: '.tab-importHistory',
        content: t('tour.settings.importHistory', 'See all data imports for your accounts.'),
        placement: 'bottom',
        disableBeacon: true,
        data: { open: '.tab-importHistory' },
      },
    ],
    reports: [
      {
        target: '.tab-time-report',
        content: t(
          'tour.reports.time',
          'This tab displays trading activity by time: number of transactions, their monetary value, and time-of-day distribution.',
        ),
        placement: 'bottom',
        disableBeacon: true,
        data: { open: '.tab-time-report' },
      },
      {
        target: '.tab-duration',
        content: t(
          'tour.reports.duration',
          'This tab analyzes how long your trades last, their financial impact, and how they correlate with stress levels.',
        ),
        placement: 'bottom',
        disableBeacon: true,
        data: { open: '.tab-duration' },
      },
      {
        target: '.tab-instruments',
        content: t(
          'tour.reports.instruments',
          'This tab ranks instruments by transaction count, profitability, losses, and stress. Stress-based charts require journaling.',
        ),
        placement: 'bottom',
        disableBeacon: true,
        data: { open: '.tab-instruments' },
      },
      {
        target: '.tab-psychology',
        content: t(
          'tour.reports.psychology',
          'Use this tab to connect psychology to performance: trader profile and emotion and rule-break correlations with P&L. Complete Journaling on trades to unlock meaningful insights.',
        ),
        placement: 'bottom',
        disableBeacon: true,
        data: { open: '.tab-psychology' },
      },
      {
        target: '.tab-trading-strategy',
        content: t(
          'tour.reports.strategy',
          'This tab reveals which strategies are most used, most profitable, or most stressful. Requires journaling.',
        ),
        placement: 'bottom',
        disableBeacon: true,
        data: { open: '.tab-trading-strategy' },
      },
      {
        target: '.tab-trading-confluence',
        content: t('tour.reports.tradingConfluence'),
        placement: 'bottom',
        disableBeacon: true,
        data: { open: '.tab-trading-confluence' },
      },
      {
        target: '.tab-risk-management',
        content: t(
          'tour.reports.risk',
          'This tab evaluates how you manage risks through stop-losses, risk-reward ratios, and trade sizing. Requires journaling.',
        ),
        placement: 'bottom',
        disableBeacon: true,
        data: { open: '.tab-risk-management' },
      },
      {
        target: '.tab-drawdown',
        content: t(
          'tour.reports.drawdown',
          'This tab helps you assess the depth and duration of your trading losses. It includes metrics such as average and biggest drawdowns, as well as how long and how many trades you stay in a drawdown phase.',
        ),
        placement: 'bottom',
        disableBeacon: true,
        data: { open: '.tab-drawdown' },
      },
      // {
      //   target: '.button-create-report',
      //   content: t(
      //     'tour.reports.create',
      //     'Click this button to generate a customizable PDF report based on selected charts.',
      //   ),
      //   placement: 'bottom',
      //   disableBeacon: true,
      // },
    ],
  }
}
